"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  BookOpen,
  Check,
  Clock,
  Flag,
  Hammer,
  Layers,
  Sparkles,
} from "lucide-react";
import CourseProgress from "./CourseProgress";
import { lessonKey, useLearnProgress } from "./progress";
import { panel } from "./ui";

export type CatalogCourse = {
  slug: string;
  kind: "understand" | "build";
  title: string;
  summary: string;
  outcome: string;
  minutes: number;
  lessons: string[];
};

type Filter = "all" | "understand" | "build";

const filters: { id: Filter; label: string; short?: string }[] = [
  { id: "all", label: "All courses", short: "All" },
  { id: "understand", label: "Understand" },
  { id: "build", label: "Build" },
];

/* The heading and description above the grid follow the filter, so the
   words always describe the courses showing. */
const intro: Record<
  Filter,
  { title: (total: number) => string; body: (count: number) => string }
> = {
  all: {
    title: (total) => `Browse all ${total} courses`,
    body: () =>
      "Understand courses teach the ideas and work with any AI tool. Build courses walk you through making something real in LaunchLemonade.",
  },
  understand: {
    title: () => "Understand how it works",
    body: (count) =>
      `${count} courses on what is really going on inside an AI agent, and how to get a straight answer out of one. No tool needed: read them and use what you learn anywhere.`,
  },
  build: {
    title: () => "Build it yourself",
    body: (count) =>
      `${count} hands-on courses, one per job. Each lesson ends with you doing it in your own LaunchLemonade account, so by the end you have something that works.`,
  },
};

const kindCopy = {
  understand: { label: "Understand", hint: "Read anywhere" },
  build: { label: "Build", hint: "Hands-on" },
};

/* Every course on one grid, filterable by kind. "#understand" and "#build"
   links anywhere on the page pick the filter and scroll here. */
export default function CourseCatalog({
  courses,
}: {
  courses: CatalogCourse[];
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const fromHash = () => {
      const h = window.location.hash.slice(1);
      if (h === "understand" || h === "build" || h === "courses") {
        setFilter(h === "courses" ? "all" : h);
        ref.current?.scrollIntoView({ block: "start" });
      }
    };
    fromHash();
    window.addEventListener("hashchange", fromHash);
    return () => window.removeEventListener("hashchange", fromHash);
  }, []);

  const shown = courses.filter((c) => filter === "all" || c.kind === filter);
  const first = courses[0];

  return (
    <section
      ref={ref}
      id="courses"
      className="scroll-mt-24 pb-24"
      aria-labelledby="courses-heading"
    >
      <div className="learn-container px-6 md:px-12">
        <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-accent text-[11px] font-medium uppercase tracking-[0.22em] text-eyebrow mb-3">
              The catalog
            </p>
            <h2
              id="courses-heading"
              className="fluid-h2 font-heading font-bold"
            >
              {intro[filter].title(courses.length)}
            </h2>
            <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
              {intro[filter].body(shown.length)}
            </p>
          </div>

          <div
            className="inline-flex shrink-0 self-start rounded-full border p-1 md:self-auto"
            style={panel}
            role="group"
            aria-label="Filter courses"
          >
            {filters.map((f) => {
              const count =
                f.id === "all"
                  ? courses.length
                  : courses.filter((c) => c.kind === f.id).length;
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors sm:px-4 ${
                    active
                      ? "bg-[#ffe500] text-black"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.short ? (
                    <>
                      <span className="sm:hidden">{f.short}</span>
                      <span className="hidden sm:inline">{f.label}</span>
                    </>
                  ) : (
                    f.label
                  )}
                  <span
                    className={`rounded-full px-1.5 text-[11px] tabular-nums ${
                      active ? "bg-black/10" : "bg-[var(--inset)]"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="sr-only" aria-live="polite">
          Showing {shown.length} courses
        </p>

        <ul className="grid auto-rows-fr gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <li key={c.slug}>
              <CourseCard course={c} startHere={c.slug === first?.slug} />
            </li>
          ))}
          {filter === "all" && first ? (
            <li className="sm:col-span-2 lg:col-span-1">
              <StartTile course={first} />
            </li>
          ) : null}
        </ul>
      </div>
    </section>
  );
}

function CourseCard({
  course: c,
  startHere,
}: {
  course: CatalogCourse;
  startHere: boolean;
}) {
  const understand = c.kind === "understand";
  const { done } = useLearnProgress();
  const complete = c.lessons.every((s) => done.has(lessonKey(c.slug, s)));

  const KindIcon = understand ? BookOpen : Hammer;

  return (
    <div className="course-card group relative h-full transition-transform duration-300 motion-safe:hover:-translate-y-1">
      {/* Sits on the card's top edge, outside the Link's overflow-hidden clip.
          Hidden until hover or focus; always shown where there is no hover. */}
      <span
        className="pointer-events-none absolute -top-3 left-6 z-10 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-accent text-[10.5px] uppercase tracking-[0.14em] opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
        style={{ background: "#ffe500", color: "#000" }}
      >
        <KindIcon className="h-3 w-3" aria-hidden />
        {kindCopy[c.kind].label}
      </span>
      <Link
        href={`/learn/${c.slug}`}
        className="relative flex h-full flex-col overflow-hidden rounded-3xl border transition-[border-color,box-shadow] duration-300 hover:border-[#ffe500] hover:shadow-[0_18px_40px_-20px_hsl(45_100%_40%/0.45)]"
        style={{
          ...panel,
          ...(complete
            ? {
                borderColor:
                  "color-mix(in srgb, #ffe500 55%, var(--hairline-strong))",
              }
            : {}),
        }}
      >
        {complete ? (
          <span
            className="h-1.5 w-full shrink-0"
            style={{ background: "#ffe500" }}
            aria-hidden
          />
        ) : null}
        <span className="flex flex-1 flex-col p-6">
          <span className="mb-3 flex items-center gap-2 text-[11.5px] font-semibold">
            <span className="flex min-w-0 flex-wrap gap-1.5">
              {[kindCopy[c.kind].hint, "Beginner", "Free"].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-black/[0.06] px-2.5 py-1 text-muted-foreground [.dark_&]:bg-white/10 [.dark_&]:text-white/80"
                >
                  {tag}
                </span>
              ))}
            </span>
            {complete ? (
              <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-[#ffe500] px-2.5 py-1 text-[11px] font-bold text-black">
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
                Completed
              </span>
            ) : startHere ? (
              <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-foreground">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: "#ffe500" }}
                  aria-hidden
                />
                Start here
              </span>
            ) : null}
          </span>

          <span className="fluid-h3 mb-2 font-heading font-semibold leading-snug">
            {c.title}
          </span>
          <span className="mb-5 line-clamp-3 text-[14px] leading-relaxed text-muted-foreground [.dark_&]:text-white/80">
            {c.summary}
          </span>

          <span className="mt-auto mb-5 flex gap-2.5 text-[13px] leading-relaxed">
            <Flag
              className="mt-[3px] h-3.5 w-3.5 shrink-0"
              style={{ color: "var(--icon-gold-text)" }}
              aria-hidden
            />
            <span className="line-clamp-2">
              <span className="font-semibold">You&apos;ll end with: </span>
              <span className="text-muted-foreground [.dark_&]:text-white/80">
                {c.outcome}
              </span>
            </span>
          </span>

          <CourseProgress track={c.slug} lessons={c.lessons} className="mb-5" />

          <span
            className="flex items-center justify-between gap-3 border-t pt-4 text-[13px]"
            style={{ borderColor: "var(--hairline)" }}
          >
            <span className="flex items-center gap-4 text-muted-foreground [.dark_&]:text-white/80">
              <span className="inline-flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" aria-hidden />
                {c.lessons.length} lessons
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                {c.minutes} min
              </span>
            </span>
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 group-hover:border-[#ffe500] group-hover:bg-[#ffe500] group-hover:text-black"
              style={{ borderColor: "var(--hairline-strong)" }}
              aria-hidden
            >
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          </span>
        </span>
      </Link>
    </div>
  );
}

/* Fills the last slot of the full grid with the one decision new visitors
   most often get stuck on. */
function StartTile({ course }: { course: CatalogCourse }) {
  return (
    <div
      className="flex h-full flex-col justify-between gap-8 rounded-3xl border border-dashed p-7"
      style={{
        borderColor: "var(--hairline-strong)",
        background: "var(--inset)",
      }}
    >
      <div>
        <span
          className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl"
          style={{
            background: "var(--icon-gold-bg)",
            color: "var(--icon-gold-text)",
          }}
          aria-hidden
        >
          <Sparkles className="h-5 w-5" />
        </span>
        <p className="fluid-h3 mb-2 font-heading font-semibold leading-snug">
          Not sure where to start?
        </p>
        <p className="text-[14px] leading-relaxed text-muted-foreground">
          Begin with {course.title}. It explains the ideas every other course
          builds on, in {course.lessons.length} short lessons, with no tool or
          signup needed.
        </p>
        <ul className="mt-5 space-y-2.5 text-[13.5px]">
          {[
            `${course.lessons.length} lessons, ${course.minutes} minutes in all`,
            "A five-minute exercise at the end of every lesson",
            "Works with whichever AI tool you already use",
          ].map((point) => (
            <li key={point} className="flex items-start gap-2.5">
              <Check
                className="mt-[3px] h-3.5 w-3.5 shrink-0"
                style={{ color: "var(--icon-gold-text)" }}
                aria-hidden
              />
              {point}
            </li>
          ))}
        </ul>
      </div>
      <Link
        href={`/learn/${course.slug}`}
        className="btn-gold self-start px-6 py-3"
      >
        Start course 1
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </div>
  );
}
